import { useState } from "react";
import { FileText, ChevronDown, ChevronUp, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

interface QuotePreviewProps {
  pricePerSq: number;
  squares: number;
  wastePercent: number;
  complexityPercent: number;
  materialCost: number;
  laborCost: number;
  tearoffCost: number;
}

export function QuotePreview({ pricePerSq, squares, wastePercent, complexityPercent, materialCost, laborCost, tearoffCost }: QuotePreviewProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const orderSq = Math.ceil(squares * (1 + wastePercent / 100));
  const baseQuote = orderSq * pricePerSq;
  const adder = baseQuote * (complexityPercent / 100);
  const total = Math.round(baseQuote + adder);

  const fmt = (n: number) => `$${n.toLocaleString()}`;

  return (
    <div className="min-w-0 rounded-xl border border-border bg-card overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <FileText className="w-4 h-4 text-primary" />
        <span className="font-semibold">Quote Preview</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="rounded-lg bg-muted/40 p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Zap className="w-3.5 h-3.5 text-primary" /> Price Per Square Override
          </div>
          <div className="flex items-start justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Rate</span>
            <span className="font-semibold text-right break-words">{fmt(pricePerSq)} / SQ</span>
          </div>
          <div className="flex items-start justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Roof Area</span>
            <span className="font-semibold text-right">{orderSq} SQ</span>
          </div>
        </div>

        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition"
        >
          <span>{showBreakdown ? "Hide" : "View"} itemized cost breakdown (reference only)</span>
          {showBreakdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {showBreakdown && (
          <div className="rounded-lg border border-border bg-background/50 p-3 text-xs space-y-1.5">
            <div className="flex items-start justify-between gap-4"><span className="text-muted-foreground">Material</span><span className="text-right">{fmt(materialCost)}</span></div>
            <div className="flex items-start justify-between gap-4"><span className="text-muted-foreground">Labor</span><span className="text-right">{fmt(laborCost)}</span></div>
            <div className="flex items-start justify-between gap-4"><span className="text-muted-foreground">Tear-off</span><span className="text-right">{fmt(tearoffCost)}</span></div>
            <div className="flex items-start justify-between gap-4 pt-1 border-t border-border font-medium"><span>Itemized total</span><span className="text-right">{fmt(materialCost + laborCost + tearoffCost)}</span></div>
          </div>
        )}

        <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold">Quote Total</div>
            <div className="text-[10px] text-muted-foreground break-words">{orderSq} SQ × {fmt(pricePerSq)} {complexityPercent > 0 && `+ ${complexityPercent}% adder`}</div>
          </div>
          <div className="text-2xl font-bold text-primary sm:text-right">{fmt(total)}</div>
        </div>
      </div>
    </div>
  );
}
